import React from "react";
import { useNavigate } from "react-router-dom";
import { generatePolicySummaryPDF } from "./PrintSummary";

interface PolicySuccessProps {
  clientName?: string;
  autoCarrier?: string;
  autoPolicyNumber?: string;
  monthlyPayment?: string;
  firstPaymentDate?: string;
  ancillaryProducts?: string[];
  onSendWelcomeEmail?: () => void;
  workflowData?: any;
  onNext?: (arg: { status: string }) => void;
  loading?: boolean;
}

export default function PolicySuccessScreen({
  clientName,
  autoCarrier,
  autoPolicyNumber,
  monthlyPayment,
  firstPaymentDate,
  ancillaryProducts,
  onSendWelcomeEmail,
  workflowData,
  onNext,
  loading,
}: PolicySuccessProps) {
  const navigate = useNavigate();

  // Dynamic data extraction
  const dynamicClientName = `${workflowData?.step1?.firstName || "Client"} ${workflowData?.step1?.lastName || ""}`.trim();
  const dynamicAutoCarrier = workflowData?.step4?.carrier || autoCarrier || "Selected Carrier";
  const dynamicAutoPolicyNumber = workflowData?.step9?.policyNumber || autoPolicyNumber || "Pending";
  const dynamicMonthlyPayment = `$${workflowData?.step8?.monthlyPayment || monthlyPayment || "0.00"}`;
  const dynamicFirstPaymentDate = workflowData?.step8?.firstPaymentDate || firstPaymentDate || "TBD";
  const dynamicAncillaryProducts = workflowData?.step7?.selectedAncillary || ancillaryProducts || [];

  React.useEffect(() => {
    const layoutContent = document.querySelector(".layout-content");
    if (layoutContent) {
      layoutContent.scrollTo({ top: 0, behavior: "auto" });
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);
  return (
    <div className="page-container">
      <div className="policy-success-banner mb-6">
        <div className="policy-success-banner-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="12" fill="#36c0c4" opacity="0.15"/>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" stroke="#36c0c4"/>
          </svg>
        </div>
        <div className="policy-success-banner-text">
          <h1 className="policy-success-banner-title">Policy Successfully Bound!</h1>
          <p className="policy-success-banner-desc">Your policy and products are now active. Details below.</p>
        </div>
        <div className="policy-success-banner-confetti">
          <span>🎉</span>
        </div>
      </div>
      <div className="form-grid gap-8">
        <div className="policy-success-grid">
          {/* Client & Policy Details Card */}
          <div className="policy-success-card policy-success-card-details">
            <h3 className="policy-success-card-title">Client &amp; Policy Details</h3>
            <div className="client-details-grid">
              <div className="client-detail-item">
                <span className="client-detail-label">Client Name</span>
                <span className="client-detail-value">{dynamicClientName}</span>
              </div>
              <div className="client-detail-item">
                <span className="client-detail-label">Auto Carrier</span>
                <span className="client-detail-value">{dynamicAutoCarrier}</span>
              </div>
              <div className="client-detail-item">
                <span className="client-detail-label">Monthly Payment</span>
                <span className="client-detail-value">{dynamicMonthlyPayment}</span>
              </div>
              <div className="client-detail-item">
                <span className="client-detail-label">First Payment Due</span>
                <span className="client-detail-value">{dynamicFirstPaymentDate}</span>
              </div>
              <div className="client-detail-item client-detail-full">
                <span className="client-detail-label">Auto Policy Number</span>
                <span className="client-detail-value">{dynamicAutoPolicyNumber}</span>
              </div>
              {dynamicAncillaryProducts.length > 0 && (
                <div className="client-detail-item client-detail-full">
                  <span className="client-detail-label">Ancillary Products</span>
                  <div className="policy-success-badge-list" style={{ marginTop: "0.4rem" }}>
                    {dynamicAncillaryProducts.map((prod: string) => (
                      <span key={prod} className="policy-success-badge">{prod}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Next Steps Section */}
          <div className="next-steps-strip">
            <p className="next-steps-strip-title">Next Steps</p>
            <div className="next-steps-strip-items">
              <div className="next-step-strip-item">
                <span className="next-step-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="currentColor"/></svg>
                </span>
                <span>Check your email for policy documents and welcome kit.</span>
              </div>
              <div className="next-step-strip-divider" />
              <div className="next-step-strip-item">
                <span className="next-step-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" stroke="currentColor"/></svg>
                </span>
                <span>Renewal reminder 30 days before expiration ({workflowData?.step4?.policyExpirationDate || "TBD"}).</span>
              </div>
              <div className="next-step-strip-divider" />
              <div className="next-step-strip-item">
                <span className="next-step-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" stroke="currentColor"/></svg>
                </span>
                <span>Contact support if you have questions about your coverage.</span>
              </div>
            </div>
          </div>
        </div>
        <div className="form-actions flex justify-between mt-8">
          <button type="button" className="btn btn-primary" onClick={() => generatePolicySummaryPDF(workflowData)}>
            <span> Print Summary</span>
          </button>
          <button type="button" className="btn btn-primary" onClick={() => navigate("/search-client")}>
            Start New Quote
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M9 5l7 7-7 7" stroke="currentColor" />
            </svg>
          </button>
          {/* <button type="button" className="btn btn-primary" onClick={handleContinue}>
            {loading ? (
              "Saving..."
            ) : (
              <>
                Mark as Complete
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M9 5l7 7-7 7" stroke="currentColor" />
                </svg>
              </>
            )}
          </button> */}
        </div>
      </div>
    </div>
  );
}
